/**
 * Flowcharts from text: parsing the step list, sizing each ISO 5807 symbol around its
 * words, and handing the graph to the layered layout.
 *
 * One line is one step, or a chain of steps joined by arrows. Lines with no arrow are
 * linked top to bottom; a line that starts with an arrow continues from the step above it
 * (that is how a decision gets its Yes and No branches). The shape of a step comes from
 * the brackets around it, or from its words: a question mark makes a decision, and
 * Start/End a terminator.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  truncateText,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import {
  type ChainLine,
  nodeKey,
  parseChainLine,
} from "@/lib/tool-kit/diagrams-more/graph-text";
import {
  type LayeredDirection,
  layoutLayered,
  type PlacedEdge,
} from "@/lib/tool-kit/diagrams-more/layered";

export type FlowShape =
  | "terminal"
  | "process"
  | "decision"
  | "data"
  | "predefined"
  | "preparation"
  | "database"
  | "document"
  | "connector";

/** Display order: the ones people use most come first. */
export const FLOW_SHAPES: readonly FlowShape[] = [
  "terminal",
  "process",
  "decision",
  "data",
  "predefined",
  "preparation",
  "database",
  "document",
  "connector",
];

/** The bracket notation for each symbol, as the page documents it. */
export const FLOW_MARKERS: Record<FlowShape, string> = {
  terminal: "(text)",
  process: "[text]",
  decision: "{text}",
  data: "[/text/]",
  predefined: "[[text]]",
  preparation: "<text>",
  database: "[(text)]",
  document: "~text~",
  connector: "((text))",
};

const MARKERS: readonly { pattern: RegExp; shape: FlowShape }[] = [
  { pattern: /^\(\((.+)\)\)$/, shape: "connector" },
  { pattern: /^\[\((.+)\)\]$/, shape: "database" },
  { pattern: /^\[\[(.+)\]\]$/, shape: "predefined" },
  { pattern: /^\[\/(.+)\/\]$/, shape: "data" },
  { pattern: /^\[(.+)\]$/, shape: "process" },
  { pattern: /^\((.+)\)$/, shape: "terminal" },
  { pattern: /^\{(.+)\}$/, shape: "decision" },
  { pattern: /^<(.+)>$/, shape: "preparation" },
  { pattern: /^~(.+)~$/, shape: "document" },
];

const QUESTION_END = /[?？؟]$/;

/** The English words that make a lone step a terminator; they are always recognised. */
export const TERMINAL_WORDS: readonly string[] = [
  "start",
  "begin",
  "end",
  "stop",
  "finish",
  "exit",
];
const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;
const SPACES = /\s+/;

const terminalPatterns = new Map<string, RegExp>();

/**
 * The pattern for a whole line that is a terminator word: one of the English words or the
 * given localized ones, in any capitals. Built once per distinct word list.
 */
function terminalPattern(extra: readonly string[]): RegExp {
  const words = [...TERMINAL_WORDS, ...extra]
    .map((word) => word.trim())
    .filter((word) => word !== "");
  const key = words.join("\u0000");
  const cached = terminalPatterns.get(key);
  if (cached) {
    return cached;
  }
  const alternatives = words
    .map((word) =>
      word
        .split(SPACES)
        .map((part) => part.replace(REGEX_SPECIALS, "\\$&"))
        .join("\\s+")
    )
    .sort((a, b) => b.length - a.length)
    .join("|");
  const pattern = new RegExp(`^(?:${alternatives})$`, "iu");
  terminalPatterns.set(key, pattern);
  return pattern;
}

export interface FlowNode {
  id: string;
  text: string;
  shape: FlowShape;
  /** The 1-based line where the step first appears. */
  line: number;
  explicit: boolean;
}

export interface FlowEdge {
  from: string;
  to: string;
  label: string;
  line: number;
}

export type FlowWarningKind =
  | "selfLoop"
  | "repeat"
  | "emptyStep"
  | "noStepAbove"
  | "tooMany";

export interface FlowWarning {
  kind: FlowWarningKind;
  line: number;
  text: string;
}

export interface FlowParse {
  nodes: FlowNode[];
  edges: FlowEdge[];
  warnings: FlowWarning[];
}

const LINE_BREAK = /\r\n|\r|\n/;

/** A step's words and the shape its brackets ask for, if any. */
export function readStep(raw: string): {
  text: string;
  shape: FlowShape | null;
} {
  const trimmed = raw.trim();
  for (const { pattern, shape } of MARKERS) {
    const match = pattern.exec(trimmed);
    if (match) {
      return { text: match[1].trim(), shape };
    }
  }
  return { text: trimmed, shape: null };
}

/**
 * The shape a step with no brackets gets from its words. `terminalWords` are the page
 * language's Start/End words, on top of the English ones.
 */
export function inferShape(
  text: string,
  terminalWords: readonly string[] = []
): FlowShape {
  if (QUESTION_END.test(text)) {
    return "decision";
  }
  if (terminalPattern(terminalWords).test(text)) {
    return "terminal";
  }
  return "process";
}

interface Draft {
  nodes: FlowNode[];
  byKey: Map<string, number>;
  edges: (FlowEdge & { auto: boolean })[];
  warnings: FlowWarning[];
  limit: number;
  terminalWords: readonly string[];
}

function stepNode(draft: Draft, raw: string, line: number): number | null {
  const { text, shape } = readStep(raw);
  if (text === "") {
    return null;
  }
  const key = nodeKey(text);
  const known = draft.byKey.get(key);
  if (known !== undefined) {
    if (shape) {
      draft.nodes[known].shape = shape;
      draft.nodes[known].explicit = true;
    }
    return known;
  }
  if (draft.nodes.length >= draft.limit) {
    draft.warnings.push({ kind: "tooMany", line, text });
    return null;
  }
  draft.nodes.push({
    id: `s${draft.nodes.length}`,
    text,
    shape: shape ?? "process",
    line,
    explicit: shape !== null,
  });
  draft.byKey.set(key, draft.nodes.length - 1);
  return draft.nodes.length - 1;
}

function addEdge(
  draft: Draft,
  from: number,
  to: number,
  label: string,
  line: number,
  auto: boolean
): void {
  const a = draft.nodes[from];
  const b = draft.nodes[to];
  if (from === to) {
    draft.warnings.push({ kind: "selfLoop", line, text: a.text });
    return;
  }
  if (draft.edges.some((edge) => edge.from === a.id && edge.to === b.id)) {
    draft.warnings.push({
      kind: "repeat",
      line,
      text: `${a.text} → ${b.text}`,
    });
    return;
  }
  draft.edges.push({ from: a.id, to: b.id, label, line, auto });
}

interface Cursor {
  /** Last step of the latest line that did not begin with an arrow. */
  anchor: number | null;
  /** The step of the previous line when that line was a lone step. */
  previousPlain: number | null;
}

/** Link one line's steps: from the anchor, from the line above, and along its arrows. */
function linkLine(
  draft: Draft,
  cursor: Cursor,
  chain: ChainLine,
  ids: (number | null)[],
  line: number,
  raw: string
): void {
  const plain =
    chain.lead === null &&
    chain.items.length === 1 &&
    chain.arrows.length === 0;
  if (chain.lead) {
    if (cursor.anchor === null) {
      draft.warnings.push({ kind: "noStepAbove", line, text: raw.trim() });
    } else if (ids[0] !== null) {
      addEdge(draft, cursor.anchor, ids[0], chain.lead.label, line, false);
    }
  } else if (plain && cursor.previousPlain !== null && ids[0] !== null) {
    addEdge(draft, cursor.previousPlain, ids[0], "", line, true);
  }
  for (let i = 0; i + 1 < ids.length; i++) {
    const [from, to] = [ids[i], ids[i + 1]];
    if (from !== null && to !== null) {
      addEdge(draft, from, to, chain.arrows[i]?.label ?? "", line, false);
    }
  }
  if (!chain.lead) {
    cursor.anchor = ids.at(-1) ?? null;
  }
  cursor.previousPlain = plain ? (ids[0] ?? null) : null;
}

/**
 * Read the step list. Steps beyond `limit` are left out with a warning. A line that is
 * only one of `terminalWords` (the page language's Start/End words) becomes a terminator,
 * as the English ones always do.
 */
export function parseFlowchart(
  text: string,
  limit = 60,
  terminalWords: readonly string[] = []
): FlowParse {
  const draft: Draft = {
    nodes: [],
    byKey: new Map(),
    edges: [],
    warnings: [],
    limit,
    terminalWords,
  };
  const cursor: Cursor = { anchor: null, previousPlain: null };
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const line = index + 1;
    const chain = parseChainLine(raw);
    if (chain.empty > 0 || (chain.items.length === 0 && chain.lead)) {
      draft.warnings.push({ kind: "emptyStep", line, text: raw.trim() });
    }
    if (chain.items.length === 0) {
      continue;
    }
    const ids = chain.items.map((item) => stepNode(draft, item, line));
    linkLine(draft, cursor, chain, ids, line, raw);
  }
  return finish(draft);
}

/** Resolve shapes from words, then drop automatic links out of a finished End. */
function finish(draft: Draft): FlowParse {
  for (const node of draft.nodes) {
    if (!node.explicit) {
      node.shape = inferShape(node.text, draft.terminalWords);
    }
  }
  const incoming = new Map<string, number>();
  const edges: FlowEdge[] = [];
  const byId = new Map(draft.nodes.map((node) => [node.id, node]));
  for (const edge of draft.edges) {
    const source = byId.get(edge.from);
    const ended =
      edge.auto &&
      source?.shape === "terminal" &&
      (incoming.get(edge.from) ?? 0) > 0;
    if (ended) {
      continue;
    }
    incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
    edges.push({
      from: edge.from,
      to: edge.to,
      label: edge.label,
      line: edge.line,
    });
  }
  return { nodes: draft.nodes, edges, warnings: draft.warnings };
}

/* ------------------------------------------------------------------ sizing */

export const FLOW_FONT = 13;
export const LABEL_FONT = 12;
const LINE = FLOW_FONT * LINE_HEIGHT;
const STYLE: TextStyle = { fontSize: FLOW_FONT };
const MAX_LINES = 4;
const DIAMOND_RATIO = 0.55;
/** How far the slanted sides of the data symbol lean. */
export const DATA_SKEW = 14;

export interface FlowSymbol {
  lines: string[];
  width: number;
  height: number;
  /** Where the text block is centred, relative to the middle of the symbol. */
  textShift: number;
}

function wrapFor(
  text: string,
  maxWidth: number
): { lines: string[]; width: number } {
  const lines = wrapText(text, maxWidth, STYLE, MAX_LINES);
  return { lines, width: Math.ceil(maxTextWidth(lines, STYLE)) };
}

/** The symbol for a step: its wrapped words and the size that holds them. */
export function sizeSymbol(text: string, shape: FlowShape): FlowSymbol {
  const wrapAt = shape === "decision" ? 108 : 156;
  const { lines, width } = wrapFor(text, wrapAt);
  const textHeight = lines.length * LINE;
  const box = (extraW: number, extraH: number, minW: number, minH: number) => ({
    lines,
    width: Math.max(minW, width + extraW),
    height: Math.max(minH, Math.ceil(textHeight + extraH)),
    textShift: 0,
  });
  switch (shape) {
    case "terminal":
      return box(40, 18, 96, 38);
    case "decision": {
      const w = Math.max(116, width + 12 + (textHeight + 8) / DIAMOND_RATIO);
      return {
        lines,
        width: Math.ceil(w),
        height: Math.ceil(Math.max(64, w * DIAMOND_RATIO)),
        textShift: 0,
      };
    }
    case "data":
      return box(56, 20, 100, 38);
    case "predefined":
      return box(48, 20, 100, 38);
    case "preparation":
      return box(52, 20, 100, 38);
    case "database": {
      const base = box(30, 36, 84, 56);
      return { ...base, textShift: 5 };
    }
    case "document": {
      const base = box(30, 30, 90, 46);
      return { ...base, textShift: -4 };
    }
    case "connector": {
      const d = Math.ceil(Math.max(34, width + 18, textHeight + 18));
      return { lines, width: d, height: d, textShift: 0 };
    }
    default:
      return box(30, 20, 84, 38);
  }
}

export interface PlacedStep {
  node: FlowNode;
  symbol: FlowSymbol;
  rect: Rect;
}

export interface PlacedLink {
  edge: FlowEdge;
  path: PlacedEdge;
  /** The label text after truncation, if the link has one. */
  labelText: string;
}

export interface FlowLayout {
  width: number;
  height: number;
  steps: PlacedStep[];
  links: PlacedLink[];
}

const LABEL_STYLE: TextStyle = { fontSize: LABEL_FONT };
const LABEL_MAX = 110;

/** Lay the parsed flowchart out. */
export function layoutFlowchart(
  parse: FlowParse,
  direction: LayeredDirection
): FlowLayout {
  const symbols = parse.nodes.map((node) => sizeSymbol(node.text, node.shape));
  const labels = parse.edges.map((edge) =>
    edge.label === "" ? null : truncateText(edge.label, LABEL_MAX, LABEL_STYLE)
  );
  const layout = layoutLayered(
    parse.nodes.map((node, i) => ({
      id: node.id,
      width: symbols[i].width,
      height: symbols[i].height,
    })),
    parse.edges.map((edge, i) => {
      const text = labels[i];
      return {
        from: edge.from,
        to: edge.to,
        label: text
          ? {
              width: Math.ceil(estimateTextWidth(text, LABEL_STYLE)) + 12,
              height: 18,
            }
          : null,
      };
    }),
    { direction }
  );
  const rects = new Map(layout.nodes.map((node) => [node.id, node]));
  return {
    width: layout.width,
    height: layout.height,
    steps: parse.nodes.map((node, i) => {
      const placed = rects.get(node.id) as Rect;
      return {
        node,
        symbol: symbols[i],
        rect: {
          x: placed.x,
          y: placed.y,
          width: placed.width,
          height: placed.height,
        },
      };
    }),
    links: layout.edges.map((path) => ({
      edge: parse.edges[path.index],
      path: seatOnSymbols(path, parse.nodes, rects),
      labelText: labels[path.index] ?? "",
    })),
  };
}

/**
 * A loop-back arrow meets the middle of a node's right-hand side. The data symbol leans,
 * so the middle of its side is a little inside the box around it; move the end there.
 */
function seatOnSymbols(
  path: PlacedEdge,
  nodes: readonly FlowNode[],
  rects: Map<string, Rect>
): PlacedEdge {
  const points = path.points.map((point): [number, number] => [...point]);
  for (const [which, id] of [
    [0, path.from],
    [points.length - 1, path.to],
  ] as const) {
    const rect = rects.get(id);
    const node = nodes.find((candidate) => candidate.id === id);
    const point = points[which];
    if (
      rect &&
      node?.shape === "data" &&
      Math.abs(point[0] - (rect.x + rect.width)) < 0.2 &&
      point[1] > rect.y + 1 &&
      point[1] < rect.y + rect.height - 1
    ) {
      point[0] -= DATA_SKEW / 2;
    }
  }
  return { ...path, points };
}

/* ------------------------------------------------------- text alternative */

export interface FlowRow {
  number: number;
  text: string;
  shape: FlowShape;
  /** "Yes: Print the result; No: Show an error", or just the next step's words. */
  next: string;
}

/** One row per step, with where it leads — the table and "Copy outline" use these. */
export function flowRows(parse: FlowParse): FlowRow[] {
  const byId = new Map(parse.nodes.map((node) => [node.id, node]));
  return parse.nodes.map((node, i) => ({
    number: i + 1,
    text: node.text,
    shape: node.shape,
    next: parse.edges
      .filter((edge) => edge.from === node.id)
      .map((edge) => {
        const target = byId.get(edge.to)?.text ?? "";
        return edge.label === "" ? target : `${edge.label}: ${target}`;
      })
      .join("; "),
  }));
}

/** The steps as plain text, one per line: `3. Is n even? → Yes: Print even; No: Print odd`. */
export function flowOutline(parse: FlowParse): string {
  return flowRows(parse)
    .map(
      (row) =>
        `${row.number}. ${row.text}${row.next === "" ? "" : ` → ${row.next}`}`
    )
    .join("\n");
}

/** Counts shown above the chart. */
export function flowCounts(parse: FlowParse): {
  steps: number;
  decisions: number;
  links: number;
} {
  return {
    steps: parse.nodes.length,
    decisions: parse.nodes.filter((node) => node.shape === "decision").length,
    links: parse.edges.length,
  };
}
